import type { ContentUsage, OrgContentSource } from "@/types/api";
import { http } from "./http";

/**
 * The partner's document library.
 *
 * Separate from billing and payment methods: this is what an organization
 * teaches from, not what it owes. Uploads go through the Gateway rather than
 * straight to storage, so size, type and quota are all checked before a byte is
 * stored.
 */
export const contentService = {
	list: (signal?: AbortSignal) =>
		http.get<{ sources: OrgContentSource[]; usage: ContentUsage }>(
			"/api/org/content",
			{ signal },
		),

	/** One document, for polling while it is being ingested. */
	get: (id: string, signal?: AbortSignal) =>
		http.get<{ source: OrgContentSource }>(`/api/org/content/${id}`, {
			signal,
		}),

	addUrl: (body: { url: string; title?: string; collection?: string }) =>
		http.post<{ source: OrgContentSource; duplicate: boolean }>(
			"/api/org/content",
			{ kind: "url", ...body },
		),

	addText: (body: { text: string; title?: string; collection?: string }) =>
		http.post<{ source: OrgContentSource; duplicate: boolean }>(
			"/api/org/content",
			{ kind: "text", ...body },
		),

	/**
	 * Multipart, so the file never becomes a base64 string a third larger than
	 * itself on its way through a JSON body.
	 */
	addFile: (file: File, opts: { title?: string; collection?: string } = {}) => {
		const form = new FormData();
		form.append("file", file);
		if (opts.title) form.append("title", opts.title);
		if (opts.collection) form.append("collection", opts.collection);
		return http.postForm<{ source: OrgContentSource; duplicate: boolean }>(
			"/api/org/content",
			form,
		);
	},

	retry: (id: string) =>
		http.post<{ accepted: true }>(`/api/org/content/${id}/retry`, {}),

	remove: (id: string) => http.del<{ ok: true }>(`/api/org/content/${id}`),
};
